import { useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  CreateBusinessPartnerRequestDto,
  CreateBusinessPartnerResponseDto,
  findContactsByCreatedByIdQueryKey,
  FindContactsByCreatedByIdResponseDto,
  useUpdateBusinessPartner,
} from "~/gen/api"
import {
  organizationRequestDtoCountryEnum as organizationCountryEnum,
  type OrganizationRequestDtoCountryEnumKey,
} from "~/gen/api/types/OrganizationRequestDto.ts"
import type { UpdateBusinessPartnerRequestDto } from "~/gen/api/types/UpdateBusinessPartnerRequestDto.ts"
import type { BusinessPartnerRepositoryAddressSnapshot } from "~/pages/Admin/components/form.types"
import { SupplierFormValues } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/columns"
import { BusinessPartnerKind } from "~/pages/Brand/Suppliers/SuppliersRepository/businessPartnerKind"
import { toCreateBusinessPartnerRepositoryRecordPayload } from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/utils"
import { DEFAULT_BUSINESS_RELATIONSHIP_POLICY } from "~/pages/Brand/Suppliers/SuppliersRepository/utils/useFormFieldConfig"
import { useCreateContactMutation } from "~/pages/SupplierPortal/Contacts/hooks/useCreateContactMutation"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

const isOrganizationRequestDtoCountryKey = (
  value: string | null | undefined
): value is OrganizationRequestDtoCountryEnumKey => {
  return (
    typeof value === "string" &&
    (Object.values(organizationCountryEnum) as string[]).includes(value)
  )
}

const organizationCountryFromApi = (
  value: string | null | undefined
): OrganizationRequestDtoCountryEnumKey => {
  if (isOrganizationRequestDtoCountryKey(value)) {
    return value
  }
  return organizationCountryEnum.FR
}

const hasPersistedContactId = (
  form: SupplierFormValues
): form is UpdateBusinessPartnerRequestDto & { contactId: string } => {
  return "contactId" in form && form.contactId !== ""
}

type UseContactFormProps = {
  initialValues?: SupplierFormValues
  onContactCreated?: (result: CreateBusinessPartnerResponseDto) => void
  organizationId: string
}
export const contactToSupplierMapping = (
  contact: FindContactsByCreatedByIdResponseDto
): SupplierFormValues => {
  return {
    contactId: contact.id,
    businessRelationshipId: contact.user.organization.businessRelationship.id,
    businessPartnerKind: contact.kind,
    canAnswerOnBehalf:
      contact.user.organization.businessRelationship.canAnswerOnBehalf ??
      DEFAULT_BUSINESS_RELATIONSHIP_POLICY.canAnswerOnBehalf,
    supplierId: contact.user.id.toString(),
    name: contact.user.name,
    email: contact.user.email,
    preferedLangageIso6391: contact.user.preferedLangageIso6391,
    organization: {
      businessRelationship: contact.user.organization.businessRelationship,
      name: contact.user.organization.name,
      street: contact.user.organization.street ?? "",
      city: contact.user.organization.city ?? "",
      postalCode: contact.user.organization.postalCode ?? "",
      country: organizationCountryFromApi(contact.user.organization.country),
      registrationId: contact.user.organization.registrationId,
      coordinates: contact.user.organization.coordinates ?? null,
    },
  }
}

const defaultContact: SupplierFormValues = {
  contactId: "",
  businessRelationshipId: "",
  canAnswerOnBehalf: DEFAULT_BUSINESS_RELATIONSHIP_POLICY.canAnswerOnBehalf,
  supplierId: "",
  name: "",
  email: "",
  preferedLangageIso6391: "EN",
  organization: {
    businessRelationship: {
      isActive: true,
      supplierCode: null,
      type: null,
    },
    name: "",
    street: "",
    city: "",
    postalCode: "",
    country: organizationCountryEnum.FR,
    registrationId: [],
    coordinates: null,
  },
}

const useContactForm = ({
  initialValues,
  onContactCreated,
  organizationId,
}: UseContactFormProps) => {
  const { t } = useTranslation("traceability")

  const [contact, setContact] = useState<SupplierFormValues>(
    initialValues ?? defaultContact
  )
  const contactId =
    "contactId" in contact && contact.contactId !== "" ? contact.contactId : ""

  const { mutate: createContactMutate, isPending: isCreating } =
    useCreateContactMutation(organizationId, {
      onSuccess: (data) => {
        onContactCreated?.(data)
        setContact((oldContact) => ({
          ...oldContact,
          contactId: data.kind === BusinessPartnerKind.CONTACT ? data.id : "",
          businessRelationshipId:
            data.kind === BusinessPartnerKind.ON_BEHALF
              ? data.id
              : oldContact.businessRelationshipId,
        }))
      },
    })

  const createContact = (
    values: CreateBusinessPartnerRequestDto,
    options?: {
      repositoryRecord?: BusinessPartnerRepositoryAddressSnapshot
      duplicateCandidateId?: string
    }
  ) => {
    const payload: CreateBusinessPartnerRequestDto = {
      ...values,
    }
    if (options?.repositoryRecord) {
      payload.repositoryRecord = toCreateBusinessPartnerRepositoryRecordPayload(
        options.repositoryRecord
      )
    }
    const trimmedDuplicateId = options?.duplicateCandidateId?.trim()
    if (
      trimmedDuplicateId !== undefined &&
      trimmedDuplicateId !== null &&
      trimmedDuplicateId !== ""
    ) {
      payload.duplicateCandidateId = trimmedDuplicateId
    }
    createContactMutate(payload)
  }

  const { mutate: updateBusinessPartnerMutate, isPending: isUpdating } =
    useUpdateBusinessPartner({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: findContactsByCreatedByIdQueryKey(organizationId),
          })
          await queryClient.invalidateQueries({
            queryKey: ["tags"],
          })

          const isOnBehalfPartner =
            contact.businessPartnerKind === BusinessPartnerKind.ON_BEHALF

          showSuccessNotification({
            message: isOnBehalfPartner
              ? t("contactModal.messages.update.onBehalfSuccess")
              : t("contactModal.messages.update.success", {
                  email: contact.email,
                }),
            title: isOnBehalfPartner
              ? t("contactModal.messages.update.onBehalfSuccess")
              : t("contactModal.messages.update.success", {
                  email: contact.email,
                }),
          })
        },
      },
    })

  const updateBusinessPartner = (values: UpdateBusinessPartnerRequestDto) => {
    updateBusinessPartnerMutate({
      id: contactId,
      data: values,
    })
  }

  const handleContact = (
    values?: SupplierFormValues,
    options?: {
      repositoryRecord?: BusinessPartnerRepositoryAddressSnapshot
      duplicateCandidateId?: string
    }
  ) => {
    const snapshot: SupplierFormValues = values
      ? { ...contact, ...values }
      : contact

    if (hasPersistedContactId(snapshot)) {
      const kind = snapshot.businessPartnerKind ?? BusinessPartnerKind.CONTACT

      if (kind === BusinessPartnerKind.ON_BEHALF) {
        updateBusinessPartner({
          kind,
          ...(snapshot.supplierId ? { supplierId: snapshot.supplierId } : {}),
          organization: snapshot.organization,
        })
        return
      }

      updateBusinessPartner({
        kind,
        supplierId: snapshot.supplierId,
        name: snapshot.name,
        email: snapshot.email,
        preferedLangageIso6391: snapshot.preferedLangageIso6391,
        organization: snapshot.organization,
        ...(snapshot.canAnswerOnBehalf !== undefined &&
        snapshot.canAnswerOnBehalf !== true
          ? { canAnswerOnBehalf: snapshot.canAnswerOnBehalf }
          : {}),
      })
    } else {
      createContact(
        snapshot.canAnswerOnBehalf === true
          ? {
              organization: snapshot.organization,
              canAnswerOnBehalf: true,
            }
          : {
              name: snapshot.name ?? "",
              email: snapshot.email ?? "",
              preferedLangageIso6391: snapshot.preferedLangageIso6391 ?? "EN",
              organization: snapshot.organization,
            },
        {
          repositoryRecord: options?.repositoryRecord,
          duplicateCandidateId: options?.duplicateCandidateId,
        }
      )
    }
  }

  const reset = () => {
    setContact(defaultContact)
  }

  return {
    contact,
    setContact,
    contactId,
    handleContact,
    reset,
    isCreating,
    isUpdating,
  }
}

export default useContactForm
